import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, TextField, Button, InputAdornment, IconButton } from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

/**
 * Password Form component
 * Form for changing user password
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data
 * @param {Function} props.handleInputChange - Function to handle input changes
 * @param {Function} props.handleSubmit - Function to handle form submission
 * @param {boolean} props.saving - Whether the form is currently saving
 * @returns {JSX.Element} The PasswordForm component
 */
const PasswordForm = ({ formData, handleInputChange, handleSubmit, saving }) => {
  const { t } = useTranslation('user');
  const [errors, setErrors] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Ensure values are never null to prevent React warnings
  const safeFormData = Object.keys(formData).reduce((acc, key) => {
    acc[key] = formData[key] === null ? '' : formData[key];
    return acc;
  }, {});

  // Validate form before submission
  const validateForm = (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate current password
    if (!safeFormData.currentPassword) {
      newErrors.currentPassword = t('forms.currentPasswordRequired');
    }

    // Validate new password
    if (!safeFormData.newPassword) {
      newErrors.newPassword = t('forms.newPasswordRequired');
    } else if (safeFormData.newPassword.length < 8) {
      newErrors.newPassword = t('forms.passwordMinLength');
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(safeFormData.newPassword)) {
      newErrors.newPassword = t('forms.passwordComplexity');
    }

    // Validate password confirmation
    if (!safeFormData.confirmPassword) {
      newErrors.confirmPassword = t('forms.confirmPasswordRequired');
    } else if (safeFormData.newPassword !== safeFormData.confirmPassword) {
      newErrors.confirmPassword = t('forms.passwordsDoNotMatch');
    }

    setErrors(newErrors);

    // If no errors, proceed with form submission
    if (Object.keys(newErrors).length === 0) {
      handleSubmit(e);
    }
  };

  // Clear validation errors when input changes
  const handleChange = (e) => {
    handleInputChange(e);
    setErrors({
      ...errors,
      [e.target.name]: undefined,
    });
  };

  return (
    <form onSubmit={validateForm}>
      <Grid
        container
        spacing={3}
      >
        <Grid
          item
          xs={12}
        >
          <Typography
            variant="h6"
            gutterBottom
          >
            {t('forms.changePassword')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {t('forms.passwordRequirements')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={8}
        >
          <TextField
            label={t('forms.currentPassword')}
            name="currentPassword"
            type={showCurrentPassword ? 'text' : 'password'}
            value={safeFormData.currentPassword}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.currentPassword}
            helperText={errors.currentPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        />
        <Grid
          item
          xs={12}
          md={8}
        >
          <TextField
            label={t('forms.newPassword')}
            name="newPassword"
            type={showNewPassword ? 'text' : 'password'}
            value={safeFormData.newPassword}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        />
        <Grid
          item
          xs={12}
          md={8}
        >
          <TextField
            label={t('forms.confirmNewPassword')}
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={safeFormData.confirmPassword}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<LockIcon />}
            disabled={saving}
          >
            {saving ? t('forms.updating') : t('forms.updatePassword')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default PasswordForm;
